/**
 * `<AnimatePresence mode="wait">` for React Native.
 *
 * framer-motion's "wait" mode is a two-beat hand-off: the outgoing child plays
 * its exit to completion, and only then does the incoming child enter. React
 * gives neither beat for free — swapping a `key` replaces the subtree in one
 * commit, so the old content disappears instantly and the two animations that
 * should have been sequential collapse into one.
 *
 * This keeps the outgoing node in state, animates it out, and swaps only when
 * that finishes. Each node carries the offsets it should leave by, so a step
 * sliding out sideways and a pill dropping upward stay distinct — the outgoing
 * animation uses the vector that was active when it was rendered, not whatever
 * the incoming child wants.
 */

import { useEffect, useRef, useState, type FC, type ReactNode } from "react";
import { Animated, Easing } from "react-native";

/** Where a node comes from on enter, and leaves to on exit, in dp. */
export interface SwapOffsets {
  enterX?: number;
  enterY?: number;
  exitX?: number;
  exitY?: number;
}

interface AnimatedSwapProps extends SwapOffsets {
  /** Changing this plays the hand-off. Equal keys re-render in place. */
  swapKey: string | number;
  durationMs?: number;
  children: ReactNode;
}

interface Held extends SwapOffsets {
  key: string | number;
  node: ReactNode;
}

/** framer's default tween easing, and the source's 0.2s step transition. */
const DEFAULT_DURATION_MS = 200;
const SWAP_EASING = Easing.inOut(Easing.ease);

const offsetsOf = (props: SwapOffsets): SwapOffsets => ({ enterX: props.enterX ?? 0, enterY: props.enterY ?? 0, exitX: props.exitX ?? 0, exitY: props.exitY ?? 0 });

export const AnimatedSwap: FC<AnimatedSwapProps> = ({ swapKey, durationMs = DEFAULT_DURATION_MS, children, ...offsets }) => {
  const progress = useRef(new Animated.Value(1)).current;
  const [held, setHeld] = useState<Held>({ key: swapKey, node: children, ...offsetsOf(offsets) });

  // Kept in a ref so the swap effect depends only on the key: re-rendering the
  // SAME step with new props must update the content in place, never restart
  // the hand-off.
  const incoming = useRef<Held>(held);
  incoming.current = { key: swapKey, node: children, ...offsetsOf(offsets) };

  const heldKey = useRef(held.key);
  heldKey.current = held.key;

  useEffect(() => {
    if (heldKey.current === swapKey) {
      // Same node, fresh props — adopt them without animating.
      setHeld(incoming.current);
      return;
    }

    const exit = Animated.timing(progress, { toValue: 0, duration: durationMs, easing: SWAP_EASING, useNativeDriver: true });
    exit.start(({ finished }) => {
      if (!finished) return;
      setHeld(incoming.current);
      progress.setValue(0);
      Animated.timing(progress, { toValue: 1, duration: durationMs, easing: SWAP_EASING, useNativeDriver: true }).start();
    });
    return () => exit.stop();
    // Deliberately keyed on `swapKey` alone: `held` is read through the ref as
    // a comparison target, and depending on the node itself would restart the
    // hand-off on every parent render.
  }, [swapKey, durationMs, progress]);

  // Leaving uses the held node's own exit vector; arriving uses its enter one.
  // `progress` runs 0→1 in both directions, so one interpolation covers both.
  const translateX = progress.interpolate({ inputRange: [0, 1], outputRange: [held.key === swapKey ? (held.enterX ?? 0) : (held.exitX ?? 0), 0] });
  const translateY = progress.interpolate({ inputRange: [0, 1], outputRange: [held.key === swapKey ? (held.enterY ?? 0) : (held.exitY ?? 0), 0] });

  return <Animated.View style={{ opacity: progress, transform: [{ translateX }, { translateY }] }}>{held.node}</Animated.View>;
};
